import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { sharePercent, type PortalSegment } from "@/utils/portalOverview.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

const COLOURS = ["primary.main", "warning.main", "info.main", "secondary.main", "grey.400"] as const;

function colourAt(index: number): string {
  return COLOURS[index % COLOURS.length];
}

function Block({ segment, total, colour }: { readonly segment: PortalSegment; readonly total: number; readonly colour: string }) {
  const count = segment.count ?? 0;
  const blocked = segment.blocked?.count ?? 0;
  const sx = { display: "flex", width: `${sharePercent(count, total)}%`, bgcolor: colour };
  const darker = <Box sx={{ width: `${sharePercent(blocked, count)}%`, bgcolor: colour, filter: "brightness(0.55)" }} />;
  if (segment.href === undefined) return <Box sx={sx}>{darker}</Box>;
  return (
    <Link component={RouterLink} to={segment.href} tabIndex={-1} aria-label={copy.openSourcesLabel(segment.label)} sx={sx}>
      {darker}
    </Link>
  );
}

function SegmentLabel({ segment }: { readonly segment: PortalSegment }) {
  if (segment.count === null || segment.href === undefined) {
    return <Typography variant="body2">{copy.shareLabel(segment.label, segment.count)}</Typography>;
  }
  return (
    <>
      <Link component={RouterLink} to={segment.href} variant="body2">{copy.shareLabel(segment.label, segment.count)}</Link>
      {segment.blocked && segment.blocked.count > 0 && (
        <Link component={RouterLink} to={segment.blocked.href} variant="body2" color="text.secondary">
          {copy.blockedLabel(segment.blocked.count)}
        </Link>
      )}
    </>
  );
}

/** One bar of parts, each opening Sources; a darker share inside a part is the share Shield blocks. */
export default function SplitBar({ label, segments }: { readonly label: string; readonly segments: readonly PortalSegment[] }) {
  const total = segments.reduce((sum, segment) => sum + (segment.count ?? 0), 0);
  return (
    <Box>
      {total > 0 && (
        <Box sx={{ display: "flex", height: 12, borderRadius: 1, overflow: "hidden", my: 1.5 }}>
          {segments.map((segment, index) => (segment.count ? (
            <Block key={segment.key} segment={segment} total={total} colour={colourAt(index)} />
          ) : null))}
        </Box>
      )}
      <Box component="ul" aria-label={label}
        sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 1 }}>
        {segments.map((segment, index) => (
          <Box component="li" key={segment.key} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1 }}>
            <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colourAt(index) }} />
            <SegmentLabel segment={segment} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
